import { Sparkles, Check, X, AlertCircle, LogIn, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { TRIAL_DURATION_DAYS } from '../services/trialService';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
interface TrialOnboardingDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when user starts the trial */
  onStartTrial: () => void;
  /** Callback when user chooses to continue with Personal mode */
  onContinuePersonal: () => void;
  /** Whether this is a reminder (shown weekly after skipping) */
  isReminder?: boolean;
  /** Callback when reminder is dismissed (different from choosing Personal) */
  onDismissReminder?: () => void;
  /**
   * Callback when the FIRST-RUN dialog is dismissed via Escape/overlay-click
   * (a soft snooze, different from explicitly choosing Personal). Without it
   * the first-run dialog is effectively unclosable: dismiss was a no-op and
   * the dialog re-opened every boot.
   */
  onSoftDismiss?: () => void;
  /** Whether a trial start is in progress */
  isLoading?: boolean;
  /** Error message if trial start failed */
  error?: string | null;
  /** Whether the user is currently authenticated */
  isAuthenticated?: boolean;
  /** Signed-in account's email, shown in the signed-in copy */
  userEmail?: string | null;
  /** True while the signed-in account's entitlement is still being fetched */
  isResolvingEntitlement?: boolean;
  /** Callback when user wants to sign in as existing Pro user */
  onSignIn?: () => void;
}

/** List of Pro trial benefits to display */
const TRIAL_BENEFITS = [
  'Full AI Workspace with schema sync',
  'Create and manage multiple named sessions',
  'Full query history per session',
  'All Pro features for 14 days',
];

/** What happens after trial ends */
const AFTER_TRIAL_INFO = [
  'Trial expires automatically after 14 days',
  'Subscribe to continue with Pro features',
  'Or continue with Personal mode (free, non-commercial)',
];

/**
 * Dialog prompting first-time users to start a trial or choose Personal mode.
 * Also used as a weekly reminder for users who skipped the trial.
 *
 * SERVER-BACKED TRIAL:
 * Starting a trial requires authentication. When user clicks "Start Trial",
 * they'll be prompted to sign in first (AuthDialog opens). After auth,
 * the trial is created on the server with authoritative timestamps.
 */
export function TrialOnboardingDialog({
  open,
  onStartTrial,
  onContinuePersonal,
  isReminder = false,
  onDismissReminder,
  onSoftDismiss,
  isLoading = false,
  error = null,
  isAuthenticated = false,
  userEmail = null,
  isResolvingEntitlement = false,
  onSignIn,
}: TrialOnboardingDialogProps) {
  // Signed in with no Pro (the dialog closes by itself once Pro access
  // resolves): say so, and make the trial the obvious next step. Without this
  // the only visible change after "Sign in here" was a vanished footnote, and
  // users closed the dialog believing the account itself was the unlock.
  const signedInAs = userEmail ? `Signed in as ${userEmail}.` : 'Signed in.';
  const signedInDescription = isResolvingEntitlement
    ? `${signedInAs} Checking your subscription…`
    : `${signedInAs} No Pro subscription found. Start your free ${TRIAL_DURATION_DAYS}-day trial?`;

  const handleDismiss = () => {
    if (isReminder) {
      onDismissReminder?.();
    } else {
      onSoftDismiss?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isLoading && handleDismiss()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {isReminder ? 'Try Jam SQL Studio Pro' : 'Welcome to Jam SQL Studio'}
          </DialogTitle>
          <DialogDescription data-testid="trial-dialog-description">
            {isAuthenticated
              ? signedInDescription
              : isReminder
                ? 'You\'re using Personal mode. Would you like to try Pro features?'
                : `Start your free ${TRIAL_DURATION_DAYS}-day trial and unlock all Pro features.`}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Trial benefits */}
          <div>
            <p className="text-sm font-medium mb-2">Pro trial includes:</p>
            <ul className="space-y-2">
              {TRIAL_BENEFITS.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* After trial info (only for first-time users) */}
          {!isReminder && (
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                After your trial:
              </p>
              <ul className="space-y-1">
                {AFTER_TRIAL_INFO.map((info, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm text-muted-foreground"
                  >
                    <span className="shrink-0">•</span>
                    <span>{info}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Account note: what "Start Free Trial" will do next */}
          {isAuthenticated ? (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2"
              data-testid="trial-dialog-signed-in-note"
            >
              <User className="h-3 w-3 shrink-0" />
              <span>
                {isResolvingEntitlement
                  ? 'Checking this account for a Pro subscription…'
                  : 'This account has no Pro subscription yet. Start Free Trial activates it for this account, no credit card needed.'}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <LogIn className="h-3 w-3 shrink-0" />
              <span>Sign in required to start trial (free account)</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col gap-3 sm:flex-col sm:space-x-0">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onContinuePersonal} disabled={isLoading}>
              {isReminder ? (
                <>
                  <X className="h-4 w-4 mr-1" />
                  Not Now
                </>
              ) : (
                'Continue with Personal'
              )}
            </Button>
            <Button onClick={onStartTrial} disabled={isLoading}>
              {isLoading ? (
                <>
                  <RandomDotmSpinner size={16} className="mr-1" />
                  Starting Trial...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Start Free Trial
                </>
              )}
            </Button>
          </div>

          {!isAuthenticated && onSignIn && (
            <p className="text-center text-xs text-muted-foreground">
              Already a Pro subscriber?{' '}
              <button
                type="button"
                className="text-primary underline underline-offset-2 hover:text-primary/80 cursor-pointer"
                onClick={onSignIn}
                disabled={isLoading}
              >
                Sign in here
              </button>
            </p>
          )}

          <p className="text-center text-xs text-muted-foreground">
            Personal mode is free for non-commercial use — no account needed.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
